import axios from "axios";
import { useFormik } from "formik";
import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Link, useNavigate } from "react-router-dom";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { REGISTER_STORE_USER } from "src/constants/mutations";
import * as Yup from "yup";
import eyeIconVisible from "../../assests/images/landing-page/eye-open.png";
import eyeIcon from "../../assests/images/landing-page/visibility_off.png";
import Logo from "../../components/common/Logo";
import { BtnLoader } from "src/commonLoader";
import { failureToast } from "src/utils/toasts";

const MailIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const PersonIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

export const SignUp = () => {
  const navigate = useNavigate();
  const [inProgress, setInProgress] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordIcon, setConfirmPasswordIcon] = useState(false);

  const togglePassword = () => {
    setPasswordVisible(!passwordVisible);
  };
  const showConfirmPassword = () => {
    setConfirmPasswordIcon(!confirmPasswordIcon);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      employerName: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("last name is required"),
      email: Yup.string()
        .email("Must be a valid email")
        .required("Email is required"),
      phoneNumber: Yup.string().required("Phone Number is required"),
      employerName: Yup.string().required("Employer Name is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .max(255)
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[\W_]/,
          "Password must contain at least one special character"
        )
        .required("Password is required"),
      confirmPassword: Yup.string()
        .required("Please confirm your password")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),

    onSubmit: async () => {
      const { confirmPassword, ...rest } = formik.values;
      const httpOrHttps = window.location.href.split("//")[0];
      const variables = {
        createStoreUserInput: {
          ...rest,
          hostName: httpOrHttps + "//" + window.location.host,
        },
      };
    
      if (!GRAPHQL_ENDPOINT) {
        console.error("GraphQL endpoint is not defined.");
        return;
      }
    
      try {
        setInProgress(true);
        const response = await axios.post(
          GRAPHQL_ENDPOINT,
          {
            query: REGISTER_STORE_USER,
            variables: variables,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
    
        const { data } = response;
    
        if (data.errors && data.errors.length > 0) {
          const errorMessage = data.errors[0]?.message || "Unknown error occurred.";
          console.error("GraphQL error:", errorMessage);
    
          failureToast(errorMessage);
    
          setInProgress(false);
          return;
        }
    
        navigate("/account-registered");
      } catch (error) {
        console.error("Network or server error:", error);
        setInProgress(false);
    
        failureToast("Something went wrong. Please try again.");
      }
    }
  
  });

  const isButtonDisabled = !formik.isValid || !formik.dirty;

  const inputWrapperClass = (touched: boolean | undefined, error: string | undefined) =>
    `relative flex items-center h-[46px] pl-[44px] pr-4 rounded-[10px] border transition-all duration-150 ${
      touched && error
        ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
        : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
    }`;

  const inputWrapperClassWithRightPadding = (touched: boolean | undefined, error: string | undefined) =>
    `relative flex items-center h-[46px] pl-[44px] pr-12 rounded-[10px] border transition-all duration-150 ${
      touched && error
        ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
        : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
    }`;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-6 md:py-8" style={{ background: "linear-gradient(180deg, #F7F8FA 0%, #EEF1F4 100%)" }}>
      <div
        onClick={() => navigate("/")}
        className="flex items-center justify-center gap-x-2 cursor-pointer mb-6"
      >
        <Logo />
        <h1 className="text-2xl font-semibold text-[#111827]">EatsBits</h1>
      </div>

      <div className="w-full max-w-[640px] rounded-[14px] p-8 bg-white shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
        <h2 className="text-[28px] font-bold text-[#111827]" style={{ letterSpacing: "-0.01em" }}>
          Create your account
        </h2>
        <p className="text-[15px] text-[#6B7280] mt-1.5 mb-5">Get started with EatsBits in minutes</p>

        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-[#374151] mb-1.5">First name</label>
              <div className={inputWrapperClass(formik.touched.firstName, formik.errors.firstName)}>
                <span className="absolute left-4 top-1/2 -translate-y-1/2"><PersonIcon /></span>
                <input
                  className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                  type="text"
                  id="firstName"
                  name="firstName"
                  placeholder="First name"
                  value={formik.values.firstName}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </div>
              {formik.touched.firstName && formik.errors.firstName && (
                <p className="text-red-500 text-xs mt-1.5">{formik.errors.firstName}</p>
              )}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-[#374151] mb-1.5">Last name</label>
              <div className={inputWrapperClass(formik.touched.lastName, formik.errors.lastName)}>
                <span className="absolute left-4 top-1/2 -translate-y-1/2"><PersonIcon /></span>
                <input
                  className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                  type="text"
                  id="lastName"
                  name="lastName"
                  placeholder="Last name"
                  value={formik.values.lastName}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                />
              </div>
              {formik.touched.lastName && formik.errors.lastName && (
                <p className="text-red-500 text-xs mt-1.5">{formik.errors.lastName}</p>
              )}
            </div>

            <div>
              <label htmlFor="employerName" className="block text-sm font-medium text-[#374151] mb-1.5">Employer name</label>
            <div className={inputWrapperClass(formik.touched.employerName, formik.errors.employerName)}>
              <span className="absolute left-4 top-1/2 -translate-y-1/2"><BuildingIcon /></span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type="text"
                id="employerName"
                name="employerName"
                placeholder="Restaurant or business name"
                value={formik.values.employerName}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
              {formik.touched.employerName && formik.errors.employerName && (
                <p className="text-red-500 text-xs mt-1.5">{formik.errors.employerName}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-1.5">Email</label>
            <div className={inputWrapperClass(formik.touched.email, formik.errors.email)}>
              <span className="absolute left-4 top-1/2 -translate-y-1/2"><MailIcon /></span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                value={formik.values.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-500 text-xs mt-1.5">{formik.errors.email}</p>
              )}
            </div>

            <div className="col-span-2">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-[#374151] mb-1.5">Phone number</label>
            <div
              className={`rounded-[10px] border transition-all duration-150 min-h-[46px] flex items-stretch overflow-hidden ${
                formik.touched.phoneNumber && formik.errors.phoneNumber
                  ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
                  : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
              }`}
            >
              <PhoneInput
                country="us"
                onlyCountries={["us"]}
                countryCodeEditable={false}
                inputProps={{ name: "phoneNumber", id: "phoneNumber", required: true }}
                value={formik.values.phoneNumber}
                onBlur={formik.handleBlur}
                onChange={(value) => formik.setFieldValue("phoneNumber", value)}
                containerStyle={{ width: "100%", margin: 0, height: "100%" }}
                inputStyle={{
                  width: "100%",
                  height: "44px",
                  border: "none",
                  borderRadius: "0",
                  paddingLeft: "58px",
                  fontSize: "14px",
                  outline: "none",
                  background: "transparent",
                }}
                buttonStyle={{
                  width: "48px",
                  height: "44px",
                  border: "none",
                  borderRadius: "0",
                  background: "transparent",
                  padding: "0 0 0 12px",
                }}
              />
            </div>
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <p className="text-red-500 text-xs mt-1.5">{formik.errors.phoneNumber}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[#374151] mb-1.5">Password</label>
            <div className={inputWrapperClassWithRightPadding(formik.touched.password, formik.errors.password)}>
              <span className="absolute left-4 top-1/2 -translate-y-1/2"><LockIcon /></span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type={passwordVisible ? "text" : "password"}
                id="password"
                name="password"
                placeholder="••••••••"
                value={formik.values.password}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <button
                type="button"
                onClick={togglePassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                aria-label={passwordVisible ? "Hide password" : "Show password"}
              >
                <img src={passwordVisible ? eyeIconVisible : eyeIcon} alt="" className="w-5 h-5" />
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="text-red-500 text-xs mt-1.5">{formik.errors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#374151] mb-1.5">Confirm password</label>
            <div className={inputWrapperClassWithRightPadding(formik.touched.confirmPassword, formik.errors.confirmPassword)}>
              <span className="absolute left-4 top-1/2 -translate-y-1/2"><LockIcon /></span>
              <input
                className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                type={confirmPasswordIcon ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="••••••••"
                value={formik.values.confirmPassword}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <button
                type="button"
                onClick={showConfirmPassword}
                className="absolute right-3 top-1/2 -translate-y-1/2 min-w-[32px] min-h-[32px] flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity"
                aria-label={confirmPasswordIcon ? "Hide password" : "Show password"}
              >
                <img src={confirmPasswordIcon ? eyeIconVisible : eyeIcon} alt="" className="w-5 h-5" />
              </button>
            </div>
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1.5">{formik.errors.confirmPassword}</p>
            )}
            </div>

            <div className="col-span-2">
              {inProgress ? (
              <div className="flex justify-center py-3">
                <BtnLoader />
              </div>
            ) : (
              <button
                type="submit"
                disabled={isButtonDisabled}
                className={`w-full h-12 rounded-[10px] font-semibold text-[15px] text-white transition-all duration-150 ${
                  isButtonDisabled
                    ? "bg-[#FD7657] opacity-50 cursor-not-allowed"
                    : "bg-[#FF6A3D] hover:-translate-y-px hover:bg-[#E85C32] active:translate-y-0"
                }`}
              >
                Sign up
              </button>
            )}
              <p className="text-center text-xs text-[#9CA3AF] mt-3">
                By signing up, you agree to be contacted about EatsBits
              </p>
            </div>

            <p className="col-span-2 text-center text-sm text-[#6B7280] mt-5">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-[#111827] hover:text-[#FF6A3D] transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
